import {
  FreightService,
  LineItemAudience,
  LineItemType,
  MarkupCategory,
  MarkupConfigurationType,
} from '@prisma/client';

export type MarkupKind = 'AIR' | 'INLAND';

/**
 * What an Air / Inland markup applies to (PEL-3045). The markup configuration drawer sets these
 * on every new markup instead of exposing the controls.
 */
export const MARKUP_KINDS = {
  AIR: {
    line_item_types: [LineItemType.TARIFF, LineItemType.PROMO, LineItemType.SPOT, LineItemType.CONTRACT],
    applicable_freight_services: [FreightService.AFR],
  },
  INLAND: {
    line_item_types: [LineItemType.PICK_UP, LineItemType.TRANSFER],
    applicable_freight_services: [FreightService.EXW, FreightService.FCA],
  },
} satisfies Record<MarkupKind, { line_item_types: LineItemType[]; applicable_freight_services: FreightService[] }>;

/**
 * Category and child audiences a markup gets from its configuration's type. `CUSTOMER` is left
 * out on purpose: it's the per-markup "Show to customer" toggle.
 */
export const MARKUP_DEFAULTS_BY_CONFIGURATION_TYPE = {
  // Tenants always see their own markups itemized; FPs never see them bundled into airline costs
  [MarkupConfigurationType.TENANT]: {
    category: MarkupCategory.TENANT_MARKUP,
    child_itemized_for: [LineItemAudience.TENANT],
    child_excluded_for: [LineItemAudience.FULFILLMENT_PARTNER],
  },
  [MarkupConfigurationType.FULFILLMENT_PARTNER]: {
    category: MarkupCategory.FULFILLMENT_PARTNER_MARKUP,
    child_itemized_for: [LineItemAudience.FULFILLMENT_PARTNER],
    child_excluded_for: [],
  },
  // SYSTEM is always itemized for itself (see `_lineItem.isItemizedFor`)
  [MarkupConfigurationType.SYSTEM]: {
    category: MarkupCategory.SYSTEM_MARKUP,
    child_itemized_for: [],
    child_excluded_for: [],
  },
} satisfies Record<
  MarkupConfigurationType,
  { category: MarkupCategory; child_itemized_for: LineItemAudience[]; child_excluded_for: LineItemAudience[] }
>;
